import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import {
  CalendarDays, Plus, Search, Pencil, Trash2, Image, X,
  MapPin, User, Link as LinkIcon, FileText, Loader2, Calendar,
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DatePicker } from "@/components/ui/DatePicker";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { toast } from "sonner";
import {
  apiGetCalendarEvents,
  apiCreateCalendarEvent,
  apiUpdateCalendarEvent,
  apiDeleteCalendarEvent,
  apiUploadEventCoverImage,
  type CalendarEvent,
} from "@/lib/api";
import { TableSkeleton } from "@/components/PageSkeleton";

const EVENT_CATEGORIES = ["meeting", "announcement", "celebration", "company", "other"] as const;
type EventCategory = typeof EVENT_CATEGORIES[number];

const CATEGORY_COLORS: Record<string, string> = {
  meeting: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  announcement: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  celebration: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  company: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  other: "bg-muted text-muted-foreground",
  bank_holiday: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

interface EventForm {
  title: string;
  category: string;
  date: string;
  endDate: string;
  allDay: boolean;
  time: string;
  location: string;
  organizer: string;
  link: string;
  description: string;
}

const EMPTY_FORM: EventForm = {
  title: "",
  category: "company",
  date: "",
  endDate: "",
  allDay: true,
  time: "",
  location: "",
  organizer: "",
  link: "",
  description: "",
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  try { return format(parseISO(dateStr), "dd MMM yyyy"); } catch { return dateStr; }
}

export default function CorporateEventsAdmin() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState<EventForm>(EMPTY_FORM);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CalendarEvent | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["corporateEventsAdmin"],
    queryFn: apiGetCalendarEvents,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDeleteCalendarEvent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corporateEventsAdmin"] });
      qc.invalidateQueries({ queryKey: ["calendarEvents"] });
      toast.success(t("corporateEvents.deleted"));
      setDeleteTarget(null);
    },
    onError: (err: any) => toast.error(err.message ?? t("corporateEvents.deleteFailed")),
  });

  const nonHolidayEvents = events.filter((e) => e.category !== "bank_holiday");

  const filtered = nonHolidayEvents.filter((e) => {
    const matchesSearch =
      !search || e.title.toLowerCase().includes(search.toLowerCase()) ||
      (e.location ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (e.organizer ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesCat = categoryFilter === "all" || e.category === categoryFilter;
    return matchesSearch && matchesCat;
  });

  const statCounts = EVENT_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = nonHolidayEvents.filter((e) => e.category === cat).length;
    return acc;
  }, {} as Record<string, number>);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setCoverFile(null);
    setCoverPreview(null);
    setModalOpen(true);
  }

  function openEdit(event: CalendarEvent) {
    setEditing(event);
    setForm({
      title: event.title,
      category: event.category || "company",
      date: event.date,
      endDate: event.endDate ?? "",
      allDay: event.allDay,
      time: event.time ?? "",
      location: event.location ?? "",
      organizer: event.organizer ?? "",
      link: event.link ?? "",
      description: event.description ?? "",
    });
    setCoverFile(null);
    setCoverPreview(event.coverImageUrl ?? null);
    setModalOpen(true);
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setCoverFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setCoverPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return;
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        date: form.date,
        endDate: form.endDate || null,
        allDay: form.allDay,
        time: form.allDay ? null : (form.time || null),
        location: form.location.trim() || null,
        organizer: form.organizer.trim() || null,
        link: form.link.trim() || null,
        description: form.description.trim() || null,
        coverImageUrl: editing?.coverImageUrl ?? null,
        createdBy: null,
      };

      let saved: CalendarEvent;
      if (editing) {
        saved = await apiUpdateCalendarEvent(editing.id, payload);
      } else {
        saved = await apiCreateCalendarEvent(payload);
      }

      if (coverFile) {
        try {
          await apiUploadEventCoverImage(saved.id, coverFile);
        } catch {
          toast.warning(t("corporateEvents.coverUploadFailed"));
        }
      }

      qc.invalidateQueries({ queryKey: ["corporateEventsAdmin"] });
      qc.invalidateQueries({ queryKey: ["calendarEvents"] });
      toast.success(editing ? t("corporateEvents.updated") : t("corporateEvents.created"));
      setModalOpen(false);
    } catch (err: any) {
      toast.error(err.message ?? t("corporateEvents.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  const isValid = form.title.trim().length > 0 && form.date.length > 0;

  return (
    <AppLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Page header */}
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{t("corporateEvents.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("corporateEvents.subtitle")}</p>
            </div>
          </div>
          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0 gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {t("corporateEvents.addEvent")}
          </Button>
        </div>

        {/* Stat strip */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-3 sm:grid-cols-5 sm:divide-x divide-border/60">
            {EVENT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(categoryFilter === cat ? "all" : cat)}
                className={`px-4 py-3 text-left transition-colors hover:bg-muted/50 ${categoryFilter === cat ? "bg-muted/60" : ""}`}
              >
                <p className="text-xs text-muted-foreground font-medium mb-0.5">
                  {t(`corporateCalendar.categories.${cat}`)}
                </p>
                <p className="text-xl font-bold">{statCounts[cat]}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("corporateEvents.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48 h-10">
              <SelectValue placeholder={t("corporateEvents.allCategories")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("corporateEvents.allCategories")}</SelectItem>
              {EVENT_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {t(`corporateCalendar.categories.${cat}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events table */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide w-[40px]" />
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                  {t("corporateEvents.colTitle")}
                </TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden sm:table-cell">
                  {t("corporateEvents.colCategory")}
                </TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                  {t("corporateEvents.colDate")}
                </TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">
                  {t("corporateEvents.colLocation")}
                </TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">
                  {t("corporateEvents.colOrganizer")}
                </TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="p-4">
                    <TableSkeleton rows={5} cols={6} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground text-sm">
                    {search || categoryFilter !== "all"
                      ? t("corporateEvents.noResults")
                      : t("corporateEvents.noEvents")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((event) => (
                  <TableRow
                    key={event.id}
                    className="border-b border-border/40 hover:bg-muted/40 transition-colors"
                  >
                    <TableCell className="pl-4 py-3">
                      {event.coverImageUrl ? (
                        <img
                          src={event.coverImageUrl}
                          alt=""
                          className="h-8 w-8 rounded-md object-cover shrink-0"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-md bg-muted/60 flex items-center justify-center shrink-0">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      <p className="font-medium text-sm leading-tight">{event.title}</p>
                      {event.description && (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px] mt-0.5">
                          {event.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="py-3 hidden sm:table-cell">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[event.category] ?? CATEGORY_COLORS.other}`}>
                        {t(`corporateCalendar.categories.${event.category}`, { defaultValue: event.category })}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">
                      <span>{formatDate(event.date)}</span>
                      {event.endDate && event.endDate !== event.date && (
                        <span className="text-xs"> → {formatDate(event.endDate)}</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground hidden md:table-cell">
                      {event.location ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[140px]">{event.location}</span>
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground hidden lg:table-cell">
                      {event.organizer ?? "—"}
                    </TableCell>
                    <TableCell className="py-3 pr-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openEdit(event)}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(event)}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!submitting) setModalOpen(v); }}>
        <DialogContent className="sm:max-w-[640px] p-0 gap-0 overflow-hidden">
          <div className="px-6 pt-6 pb-4 bg-muted/30 border-b">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5 text-base font-semibold">
                <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <CalendarDays className="h-4 w-4 text-purple-500" />
                </div>
                {editing ? t("corporateEvents.editTitle") : t("corporateEvents.createTitle")}
              </DialogTitle>
            </DialogHeader>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
              {/* Title */}
              <FloatField
                label={t("corporateCalendar.form.titleLabel")}
                required
                placeholder={t("corporateCalendar.form.titlePlaceholder")}
                value={form.title}
                onChange={(v) => setForm((p) => ({ ...p, title: v as string }))}
              />

              {/* Category */}
              <FloatWrapper label={t("corporateCalendar.form.category")}>
                <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))} disabled={submitting}>
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {t(`corporateCalendar.categories.${cat}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatWrapper>

              {/* Dates row */}
              <div className="grid grid-cols-2 gap-3">
                <FloatWrapper label={t("corporateCalendar.form.startDate")} required>
                  <DatePicker
                    value={form.date}
                    onChange={(v) => setForm((p) => ({ ...p, date: v }))}
                    disabled={submitting}
                    className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                  />
                </FloatWrapper>
                <FloatWrapper label={t("corporateCalendar.form.endDate")}>
                  <DatePicker
                    value={form.endDate}
                    onChange={(v) => setForm((p) => ({ ...p, endDate: v }))}
                    disabled={submitting}
                    className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                  />
                </FloatWrapper>
              </div>

              {/* All-day + Time */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="allDay"
                    checked={form.allDay}
                    onCheckedChange={(v) => setForm((p) => ({ ...p, allDay: v }))}
                    disabled={submitting}
                  />
                  <Label htmlFor="allDay" className="text-sm cursor-pointer">
                    {t("corporateCalendar.form.allDayCheck")}
                  </Label>
                </div>
                {!form.allDay && (
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))}
                    disabled={submitting}
                    className="w-36 h-9"
                  />
                )}
              </div>

              {/* Location */}
              <FloatField
                label={t("corporateCalendar.form.locationLabel")}
                placeholder={t("corporateCalendar.form.locationPlaceholder")}
                value={form.location}
                onChange={(v) => setForm((p) => ({ ...p, location: v as string }))}
              />

              {/* Organizer */}
              <FloatField
                label={t("corporateCalendar.form.organizerLabel")}
                placeholder={t("corporateCalendar.form.organizerPlaceholder")}
                value={form.organizer}
                onChange={(v) => setForm((p) => ({ ...p, organizer: v as string }))}
              />

              {/* Link */}
              <FloatField
                label={t("corporateCalendar.form.linkLabel")}
                placeholder={t("corporateCalendar.form.linkPlaceholder")}
                value={form.link}
                onChange={(v) => setForm((p) => ({ ...p, link: v as string }))}
              />

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t("corporateCalendar.form.descLabel")}
                </Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder={t("corporateCalendar.form.descPlaceholder")}
                  rows={3}
                  disabled={submitting}
                  className="resize-none rounded-xl border border-[#d0d5e8]"
                />
              </div>

              {/* Cover image */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t("corporateEvents.coverLabel")}
                </Label>
                {coverPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={coverPreview}
                      alt="cover"
                      className="h-28 w-full max-w-xs object-cover rounded-xl border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => { setCoverFile(null); setCoverPreview(editing?.coverImageUrl ?? null); }}
                      className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-1.5 right-1.5 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/50 text-white text-xs hover:bg-black/70"
                    >
                      <Image className="h-3 w-3" />
                      {t("corporateCalendar.actions.changePhoto")}
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center justify-center gap-2 h-20 rounded-xl border-2 border-dashed border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors text-sm text-muted-foreground"
                  >
                    <Image className="h-4 w-4" />
                    {t("corporateEvents.uploadCover")}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverChange}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="px-6 pb-6 pt-4 border-t bg-muted/20 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} disabled={submitting}>
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={submitting || !isValid}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
              >
                {submitting ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t("common.saving")}</>
                ) : (
                  editing ? t("corporateCalendar.actions.save") : t("corporateEvents.addEvent")
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("corporateCalendar.dialogs.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("corporateCalendar.dialogs.deleteDesc", { title: deleteTarget?.title ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {t("corporateCalendar.actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
